export interface Reward {
  id: number;
  name: string;
  detail: string;
  threshold: number;
  available: boolean;
  remaining: number;
}

/** A reward definition without a specific customer's progress against it. */
export interface RewardDef {
  id: number;
  name: string;
  threshold: number;
}

export interface User {
  id: number;
  name: string;
  firstName: string;
  email: string | null;
  avatarUrl: string | null;
  role: 'customer' | 'staff';
  stamps: number;
  rewards: Reward[];
  lastVisit: string | null;
}

export interface Customer {
  id: number;
  name: string;
  email: string | null;
  stamps: number;
  discountPercent: number;
  lastVisit: string;
  memberSince: string;
  rewards: Reward[];
  /** True once this customer has a matching record in Fudo, the point of sale. */
  fudoLinked: boolean;
}

/** What became of a discount on Fudo's side when it was saved here. */
export interface FudoPushResult {
  status: 'linked' | 'created' | 'skipped' | 'failed';
  reason?: string;
}

export interface FudoMenuSyncStatus {
  at: string;
  ok: boolean;
  message: string;
  categories: number;
  items: number;
}

export interface FudoStatus {
  configured: boolean;
  intervalMinutes: number;
  menuSync: FudoMenuSyncStatus | null;
}

/** The email as a QR, for the USB reader at the Fudo register. Null when the customer has none. */
export interface ScanCode {
  email: string | null;
  image: string | null;
  configured: boolean;
}

export interface MenuItem {
  id: number;
  name: string;
  /** Already formatted for display, e.g. "$3.800". */
  price: string;
  /** The raw pesos behind `price`, for editing. */
  priceValue: number;
  note?: string;
}

export interface MenuCategory {
  id: number;
  name: string;
  items: MenuItem[];
}

export type MoveDirection = 'up' | 'down';

export interface StaffMember {
  id: number;
  identifier: string;
  /** The account behind it, once that person has signed in at least once. */
  name: string | null;
  /** True for the admin currently looking at the panel. */
  isSelf: boolean;
}

export interface Transaction {
  id: number;
  kind: 'stamp' | 'reward';
  userId: number;
  name: string;
  /** Null when the staff account that acted was later deleted. */
  staffId: number | null;
  staffName: string | null;
  reward: string | null;
  stampsAfter: number;
  when: string;
}

/** Envelope the admin lists come back in, alongside their rows. */
export type Paged<T> = T & {
  total: number;
  page: number;
  pages: number;
  pageSize: number;
};

export interface NamedCount {
  name: string;
  count: number;
}

/** Café-local calendar dates, "YYYY-MM-DD", both ends inclusive. */
export interface DateRange {
  from: string;
  to: string;
}

export interface CafeStats {
  customers: {
    total: number;
    new30: number;
    active30: number;
    unredeemedStamps: number;
    averageStamps: number;
  };
  visits: { total: number; today: number; last7: number; last30: number; perActiveDay: number };
  rewards: { total: number; last30: number; byReward: NamedCount[] };
  topCustomers: NamedCount[];
  byWeekday: NamedCount[];
  byHour: Array<{ hour: number; count: number }>;
  firstActivity: string | null;
  lastActivity: string | null;
  /** Echoes the range actually applied to topCustomers/byReward/byWeekday/byHour. */
  appliedRange: DateRange | null;
}

export interface HistoryEntry {
  id: number;
  kind: 'stamp' | 'reward';
  reward: string | null;
  stampsAfter: number;
  when: string;
}

export interface QrPayload {
  token: string;
  image: string;
  expiresAt: number;
  ttlMs: number;
}

export interface ProviderAvailability {
  google: boolean;
  facebook: boolean;
  instagram: boolean;
  whatsapp: boolean;
  email: boolean;
  /** Staff-only fallback, shown just when the server has a code configured. */
  breakGlass: boolean;
}

/** Server-sent message, already written in the app's Spanish. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly reason?: string,
    /** Set when the server will accept the same call with `force`. */
    readonly canOverride = false,
    readonly secondsRemaining = 0,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      credentials: 'same-origin',
      ...init,
      headers: {
        ...(init.body ? { 'content-type': 'application/json' } : {}),
        ...(init.headers ?? {}),
      },
    });
  } catch {
    throw new ApiError('No pudimos conectar con el servidor', 0);
  }

  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }
  }

  if (!res.ok) {
    const message =
      (body && typeof body === 'object' && typeof (body as { error?: unknown }).error === 'string'
        ? (body as { error: string }).error
        : null) ?? 'Algo salió mal';
    const detail = (body ?? {}) as {
      reason?: unknown;
      canOverride?: unknown;
      secondsRemaining?: unknown;
    };
    throw new ApiError(
      message,
      res.status,
      typeof detail.reason === 'string' ? detail.reason : undefined,
      detail.canOverride === true,
      typeof detail.secondsRemaining === 'number' ? detail.secondsRemaining : 0,
    );
  }

  return body as T;
}

export const api = {
  providers: () => request<ProviderAvailability>('/auth/providers'),
  menu: () => request<{ categories: MenuCategory[]; milkNote: string }>('/api/menu'),
  me: () => request<{ user: User | null }>('/api/me'),
  myQr: () => request<QrPayload>('/api/me/qr'),
  myHistory: () => request<{ history: HistoryEntry[] }>('/api/me/history'),

  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),

  whatsappStart: (phone: string) =>
    request<{ sent: true; phone: string; expiresInMs: number }>('/auth/whatsapp/start', {
      method: 'POST',
      body: JSON.stringify({ phone }),
    }),

  whatsappVerify: (phone: string, code: string) =>
    request<{ ok: true; role: 'customer' | 'staff' }>('/auth/whatsapp/verify', {
      method: 'POST',
      body: JSON.stringify({ phone, code }),
    }),

  emailStart: (email: string) =>
    request<{ sent: true; email: string; expiresInMs: number }>('/auth/email/start', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  emailVerify: (email: string, code: string) =>
    request<{ ok: true; role: 'customer' | 'staff' }>('/auth/email/verify', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    }),

  /** Staff fallback while email is down: admin address + the server's code. */
  breakGlass: (email: string, code: string) =>
    request<{ ok: true; role: 'customer' | 'staff' }>('/auth/break-glass', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    }),

  /** Redeems the emailed link. POST so mail scanners cannot consume it. */
  emailLink: (token: string) =>
    request<{ ok: true; role: 'customer' | 'staff' }>('/auth/email/link', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  customers: (search: string, page = 1) => {
    // Built by hand rather than with URLSearchParams, which encodes a space as
    // "+". Both decode the same server-side, but %20 is what this endpoint has
    // always sent and there is no reason to churn it.
    const parts: string[] = [];
    if (search) parts.push(`search=${encodeURIComponent(search)}`);
    if (page > 1) parts.push(`page=${page}`);
    return request<Paged<{ customers: Customer[] }>>(
      `/api/admin/customers${parts.length > 0 ? `?${parts.join('&')}` : ''}`,
    );
  },

  customer: (id: number) => request<{ customer: Customer }>(`/api/admin/customers/${id}`),

  transactions: (page = 1) =>
    request<Paged<{ transactions: Transaction[] }>>(
      `/api/admin/transactions${page > 1 ? `?page=${page}` : ''}`,
    ),

  /** One person's own history — what happened to them, plus anything they rang up as staff. */
  customerHistory: (id: number, page = 1) =>
    request<Paged<{ transactions: Transaction[] }>>(
      `/api/admin/customers/${id}/history${page > 1 ? `?page=${page}` : ''}`,
    ),

  stats: (range?: DateRange) =>
    request<{ stats: CafeStats }>(
      `/api/admin/stats${range ? `?from=${range.from}&to=${range.to}` : ''}`,
    ),

  scan: (token: string) =>
    request<{ customer: Customer }>('/api/admin/scan', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  walletAvailability: () =>
    request<{ apple: boolean; google: boolean }>('/api/me/wallet/availability'),

  googleWalletUrl: () => request<{ url: string }>('/api/me/wallet/google'),

  revokeWalletPasses: () => request<{ ok: true }>('/api/me/wallet/revoke', { method: 'POST' }),

  /** `force` overrides the anti-replay cooldown for a genuine second purchase. */
  addStamp: (id: number, force = false) =>
    request<{ customer: Customer; unlockedRewards: Reward[] }>(
      `/api/admin/customers/${id}/stamp`,
      { method: 'POST', body: JSON.stringify({ force }) },
    ),

  redeem: (id: number, rewardId: Reward['id']) =>
    request<{ customer: Customer }>(`/api/admin/customers/${id}/redeem`, {
      method: 'POST',
      body: JSON.stringify({ reward: rewardId }),
    }),

  setDiscount: (id: number, percent: number) =>
    request<{ customer: Customer; fudo: FudoPushResult }>(`/api/admin/customers/${id}/discount`, {
      method: 'PUT',
      body: JSON.stringify({ percent }),
    }),

  /** The customer's email as a QR, to scan into Fudo's customer field at the register. */
  customerScanCode: (id: number) => request<ScanCode>(`/api/admin/customers/${id}/scan-code`),

  fudoStatus: () => request<FudoStatus>('/api/admin/fudo/status'),

  /** Re-reads the carta from Fudo right now instead of waiting for the timer. */
  fudoSyncMenu: () =>
    request<{ menuSync: FudoMenuSyncStatus }>('/api/admin/fudo/sync-menu', { method: 'POST' }),

  rewards: () => request<{ rewards: RewardDef[] }>('/api/admin/rewards'),

  createReward: (name: string, threshold: number) =>
    request<{ reward: RewardDef }>('/api/admin/rewards', {
      method: 'POST',
      body: JSON.stringify({ name, threshold }),
    }),

  updateReward: (id: number, name: string, threshold: number) =>
    request<{ reward: RewardDef }>(`/api/admin/rewards/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name, threshold }),
    }),

  deleteReward: (id: number) =>
    request<{ ok: true }>(`/api/admin/rewards/${id}`, { method: 'DELETE' }),

  adminMenu: () =>
    request<{ categories: MenuCategory[]; milkNote: string }>('/api/admin/menu'),

  setMilkNote: (milkNote: string) =>
    request<{ milkNote: string }>('/api/admin/menu/milk-note', {
      method: 'PUT',
      body: JSON.stringify({ milkNote }),
    }),

  createMenuCategory: (name: string) =>
    request<{ category: { id: number; name: string } }>('/api/admin/menu/categories', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),

  updateMenuCategory: (id: number, name: string) =>
    request<{ ok: true }>(`/api/admin/menu/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    }),

  deleteMenuCategory: (id: number) =>
    request<{ ok: true }>(`/api/admin/menu/categories/${id}`, { method: 'DELETE' }),

  moveMenuCategory: (id: number, direction: MoveDirection) =>
    request<{ ok: true }>(`/api/admin/menu/categories/${id}/move`, {
      method: 'POST',
      body: JSON.stringify({ direction }),
    }),

  createMenuItem: (categoryId: number, name: string, price: number, note: string) =>
    request<{ item: { id: number } }>('/api/admin/menu/items', {
      method: 'POST',
      body: JSON.stringify({ categoryId, name, price, note }),
    }),

  updateMenuItem: (id: number, name: string, price: number, note: string) =>
    request<{ ok: true }>(`/api/admin/menu/items/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name, price, note }),
    }),

  deleteMenuItem: (id: number) =>
    request<{ ok: true }>(`/api/admin/menu/items/${id}`, { method: 'DELETE' }),

  moveMenuItem: (id: number, direction: MoveDirection) =>
    request<{ ok: true }>(`/api/admin/menu/items/${id}/move`, {
      method: 'POST',
      body: JSON.stringify({ direction }),
    }),

  staff: () => request<{ staff: StaffMember[] }>('/api/admin/staff'),

  addStaff: (identifier: string) =>
    request<{ member: StaffMember }>('/api/admin/staff', {
      method: 'POST',
      body: JSON.stringify({ identifier }),
    }),

  removeStaff: (id: number) =>
    request<{ ok: true }>(`/api/admin/staff/${id}`, { method: 'DELETE' }),
};
